import React, { FC } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';

import Button from '../../Button/Button';
import { CourseCollaboration } from '../../CourseCollaboration/CourseCollaboration';

import './FormInput.scss';

import { IFileInputPreviewProps, IFormInputProps, IKey } from './FormInput.types';

const NonStringTypes = ['switch', 'file', 'radio', 'checkbox', 'button'];

const FileInputPreview = ({ passedClassName, fileSource }: IFileInputPreviewProps) => {
  switch (passedClassName) {
    case 'CollabPreview':
      return <CourseCollaboration fileSource={fileSource} isPreview />;
    default:
      return (
        <div className={classNames('FormInput-file-preview', passedClassName)}>
          <img src={fileSource} alt="File Preview" />
        </div>
      );
  }
};

FileInputPreview.defaultProps = {
  passedClassName: undefined,
  fileSource: undefined,
};

const FileInput = ({
  fileSource,
  previewComponent,
  filePreviewClassName,
  secondaryOption,
  disabled,
  onChange,
  secondaryButton,
  fileSelectButtonText,
  formControlProps,
  children,
  additionalInfo,
  noPreview,
  accept,
}) => {
  const preview =
    fileSource && !noPreview ? (
      !previewComponent ? (
        <FileInputPreview passedClassName={filePreviewClassName} fileSource={fileSource} />
      ) : (
        previewComponent
      )
    ) : undefined;
  return (
    <>
      {preview}
      {!!secondaryOption && secondaryOption}
      <div className="file-input-button-group d-flex">
        <Button
          disabled={disabled}
          type="file"
          onFileSelect={onChange}
          className={classNames({ secondary: secondaryButton })}
          accept={accept}
        >
          {fileSelectButtonText}
          <Form.Control {...formControlProps} />
        </Button>
        {children}
      </div>
      {!!additionalInfo && additionalInfo}
    </>
  );
};

const RadioInput = ({
  radioChoices,
  disabled,
  fieldName,
  value,
  inlineChoices,
  onChange,
  readOnly,
  error,
}) => (
  <div className={classNames('RadioGroup', { 'is-invalid': !!error })}>
    {radioChoices &&
      radioChoices.map((choice) => (
        <Form.Check
          custom
          disabled={disabled || choice.disabled}
          key={choice.id || `${choice.label}-${choice.value}`}
          label={choice.label}
          name={fieldName}
          value={choice.value}
          checked={value === choice.value}
          type="radio"
          id={choice.id || `${fieldName}-${choice.label}`}
          inline={!!inlineChoices}
          onChange={onChange}
          readOnly
          isInvalid={!!error}
        />
      ))}
  </div>
);

export const FormInput: FC<IFormInputProps> = ({
  additionalInfo,
  buttonText = 'Default Button Text',
  buttonClassName,
  children,
  className,
  disabled,
  displayOnly,
  error,
  fieldName,
  filePreviewClassName,
  fileSelectButtonText = 'Select File',
  fileSource,
  previewComponent,
  noPreview,
  accept,
  formGroupClassName,
  fullWidth,
  id,
  inlineChoices,
  label = 'Input Label',
  labelWrapperClassName,
  labelClassName,
  largeSwitch,
  lastInSection,
  mediumInput,
  noLabel,
  numMin,
  numMax,
  numStep,
  placeholder,
  noPlaceholder,
  radioChoices,
  readOnly,
  required,
  rows,
  secondaryButton,
  secondaryOption,
  singleLine,
  smallInput,
  subtext,
  type = 'text',
  value,
  onChange,
  onBlur,
  labelAdditions,
  noTrimming,
}): JSX.Element => {
  let updatedLabel = label;
  if (required) {
    updatedLabel += ' *';
  }

  if (type === 'switch') {
    return (
      <Form.Group as={Row} className="PraktioSwitch">
        {!largeSwitch && (
          <Col xs={12} md={3} className="text-left">
            <Form.Label className={classNames('font-weight-bold', labelClassName)}>
              {updatedLabel}
            </Form.Label>
          </Col>
        )}
        <Col xs={12} md={{ span: fullWidth ? 12 : 9 }}>
          <Form.Check
            className={classNames('text-left', className, { checked: value, largeSwitch })}
            type="switch"
            id={id || fieldName}
            readOnly={readOnly}
            custom
          >
            <Form.Check.Input
              disabled={disabled}
              checked={!!value}
              isInvalid={!!error}
              // null onChange passed to silence DevTools console warning.
              onChange={() => null}
            />
            <Form.Check.Label className={labelClassName} onClick={disabled ? undefined : onChange}>
              {largeSwitch ? updatedLabel : ''}
            </Form.Check.Label>
          </Form.Check>
        </Col>
        <Col xs={12}>
          <Form.Control.Feedback
            type="invalid"
            className={classNames('PraktioFeedback', {
              'd-block': !!error,
              'd-none': !error,
            })}
          >
            {error}
          </Form.Control.Feedback>
        </Col>
      </Form.Group>
    );
  }

  const formControlProps: IKey = {
    type: type,
    id: id || fieldName,
    value: !NonStringTypes.includes(type) && !value ? '' : value,
    placeholder: !noPlaceholder ? placeholder || label : '',
    isInvalid: !!error,
    className: type === 'file' ? 'Button-file-input' : className,
    onChange: onChange,
    onBlur:
      type === 'text' && !noTrimming
        ? (e: React.FocusEvent<HTMLInputElement>) => {
            onChange({
              ...cloneDeep(e),
              target: { ...cloneDeep(e.target), value: (value as string)?.trim() },
            });
          }
        : onChange,
    min: numMin,
    max: numMax,
    step: numStep,
    disabled,
    readOnly,
  };

  if (type === 'textarea') {
    formControlProps.as = 'textarea';
    formControlProps.rows = rows || 3;
    formControlProps.spellCheck = true;
  }

  let inputComponent = <Form.Control {...formControlProps} />;
  switch (type) {
    case 'radio':
      inputComponent = (
        <RadioInput
          radioChoices={radioChoices}
          disabled={disabled}
          fieldName={fieldName}
          value={value}
          inlineChoices={inlineChoices}
          onChange={onChange}
          readOnly={readOnly}
          error={error}
        />
      );
      break;
    case 'file':
      inputComponent = (
        <FileInput
          fileSource={fileSource}
          previewComponent={previewComponent}
          filePreviewClassName={filePreviewClassName}
          secondaryOption={secondaryOption}
          disabled={disabled}
          onChange={onChange}
          secondaryButton={secondaryButton}
          fileSelectButtonText={fileSelectButtonText}
          formControlProps={formControlProps}
          additionalInfo={additionalInfo}
          noPreview={noPreview}
          accept={accept}
        >
          {children}
        </FileInput>
      );
      break;
    case 'button':
      inputComponent = (
        <Button onClick={onChange} className={buttonClassName} disabled={disabled}>
          {buttonText}
        </Button>
      );
      break;
    default:
      if (displayOnly) {
        inputComponent = <span className="d-flex align-items-center">{value}</span>;
      }
      break;
  }

  return (
    <Form.Group
      as={Row}
      className={classNames('PraktioFormInput', formGroupClassName, {
        'last-in-section': lastInSection,
        disabled,
      })}
    >
      {updatedLabel && (
        <Col
          xs={12}
          md={singleLine ? 3 : 12}
          className={classNames('text-left', labelWrapperClassName)}
        >
          {!noLabel && (
            <>
              <Form.Label htmlFor={id} className={classNames('d-flex', labelClassName)}>
                {updatedLabel}
              </Form.Label>
              {labelAdditions}
            </>
          )}
          {subtext && singleLine && (
            <Form.Text className="text-muted text-left mb-2">{subtext}</Form.Text>
          )}
        </Col>
      )}
      <Col
        xs={12}
        md={{ span: singleLine ? (smallInput ? 3 : mediumInput ? 6 : 9) : 12 }}
        className="text-left"
      >
        {inputComponent}
        <Form.Control.Feedback type="invalid" className="PraktioFeedback">
          {error}
        </Form.Control.Feedback>
      </Col>
      {subtext && !singleLine && (
        <Col xs={12}>
          <Form.Text className="text-muted text-left mb-2">{subtext}</Form.Text>
        </Col>
      )}
    </Form.Group>
  );
};
